import { type ReactNode } from 'react';
import {
  ArrowUpRight,
  Check,
  CheckCircle2,
  ChevronDown,
  Code2,
  FileCheck2,
  LockKeyhole,
  Send,
  ShieldCheck,
  Sparkles,
  UsersRound,
  WalletCards,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { Container } from '@/components/ui/container';
import { Reveal } from '@/components/ui/reveal';
import { Section } from '@/components/ui/section';
import { EthIcon, UsdcIcon } from '@/components/ui/token-icons';

/* -------------------------------------------------------------------------- */
/*  Product-style motion demos                                                */
/* -------------------------------------------------------------------------- */

function DemoWindow({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        'cap-demo-window flex h-[14.25rem] w-full flex-col overflow-hidden rounded-2xl border border-slate-200/80 bg-white/95 text-slate-900 shadow-[0_18px_45px_-28px_rgba(15,23,42,0.45)]',
        className
      )}
    >
      <div className="min-h-0 flex-1">{children}</div>
    </div>
  );
}

/** Manage your treasury — portfolio balance, performance, and live activity. */
export function TreasuryGraphic() {
  const assets = [
    { symbol: 'USDC', value: '31.84M', Icon: UsdcIcon },
    { symbol: 'ETH', value: '4,200', Icon: EthIcon },
  ];

  return (
    <DemoWindow>
      <div className="grid h-full grid-cols-1 sm:grid-cols-[minmax(0,1.55fr)_minmax(9.5rem,0.75fr)]">
        <div className="flex min-w-0 flex-col p-3.5 sm:p-4">
          <div className="flex items-center justify-between gap-3">
            <p className="text-[0.55rem] font-medium uppercase tracking-[0.13em] text-slate-400">
              USDC balance
            </p>
            <span className="cap-glass-chip inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-[0.5rem] font-medium text-slate-500">
              All networks <ChevronDown className="size-2.5" />
            </span>
          </div>
          <div className="mt-1.5 flex items-center gap-2">
            <UsdcIcon className="size-5 shrink-0" aria-hidden="true" />
            <span className="text-xl font-semibold tracking-tight text-slate-950 sm:text-2xl">
              31.84M{' '}
              <span className="text-xs font-medium text-slate-500">USDC</span>
            </span>
            <span className="cap-glass-success-chip ml-auto rounded-full bg-emerald-50 px-1.5 py-0.5 text-[0.5rem] font-semibold text-emerald-700">
              +2.4%
            </span>
          </div>

          <svg
            viewBox="0 0 300 68"
            preserveAspectRatio="none"
            className="mt-2 h-[4.4rem] w-full overflow-visible"
          >
            <defs>
              <linearGradient id="treasury-area" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor="#ff7a33" stopOpacity="0.22" />
                <stop offset="1" stopColor="#ff7a33" stopOpacity="0" />
              </linearGradient>
            </defs>
            <path
              d="M0 56 C18 54 24 48 40 50 C58 52 62 38 80 41 C98 44 105 31 122 34 C140 37 144 26 161 29 C178 32 188 17 207 22 C225 27 231 13 248 17 C268 21 278 8 300 9 L300 68 L0 68 Z"
              fill="url(#treasury-area)"
            />
            <path
              d="M0 56 C18 54 24 48 40 50 C58 52 62 38 80 41 C98 44 105 31 122 34 C140 37 144 26 161 29 C178 32 188 17 207 22 C225 27 231 13 248 17 C268 21 278 8 300 9"
              fill="none"
              stroke="#ff6b1a"
              strokeLinecap="round"
              strokeWidth="2"
              pathLength="1"
              className="cap-chart-line"
            />
          </svg>

          <div className="mt-auto grid grid-cols-2 gap-1.5">
            {assets.map((asset, index) => (
              <div
                key={asset.symbol}
                className="cap-asset-chip cap-glass-row flex min-w-0 items-center gap-1.5 rounded-lg border border-slate-100 bg-slate-50/80 px-2 py-1.5"
                style={{ animationDelay: `${index * 140}ms` }}
              >
                <asset.Icon className="size-4 shrink-0" aria-hidden="true" />
                <span className="min-w-0">
                  <span className="block text-[0.52rem] font-semibold text-slate-500">
                    {asset.symbol}
                  </span>
                  <span className="block truncate text-[0.55rem] font-semibold text-slate-900">
                    {asset.value}
                  </span>
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="cap-glass-pane hidden border-l border-slate-100 bg-slate-50/50 p-3.5 sm:flex sm:flex-col">
          <div className="flex items-center justify-between">
            <span className="text-[0.55rem] font-semibold text-slate-700">
              Activity
            </span>
            <span className="text-[0.52rem] text-slate-400">Just now</span>
          </div>
          <div className="mt-3 space-y-2.5">
            {[
              ['Received', '+240,000 USDC', 'bg-emerald-500'],
              ['Aave yield', '+6,820 USDC', 'bg-violet-500'],
              ['Payment', '-18,400 USDC', 'bg-slate-400'],
            ].map(([label, amount, color], index) => (
              <div
                key={label}
                className="cap-activity-row flex items-center gap-2"
                style={{ animationDelay: `${index * 650}ms` }}
              >
                <span className={cn('size-1.5 rounded-full', color)} />
                <span className="text-[0.5rem] font-medium text-slate-600">
                  {label}
                </span>
                <span className="ml-auto inline-flex items-center gap-1 text-[0.52rem] font-semibold text-slate-800">
                  <UsdcIcon className="size-2.5 shrink-0" aria-hidden="true" />
                  {amount}
                </span>
              </div>
            ))}
          </div>
          <div className="cap-glass-panel mt-auto rounded-lg border border-slate-200 bg-white p-2.5">
            <div className="flex items-center gap-1.5 text-[0.5rem] font-semibold text-slate-700">
              <Sparkles className="size-3 text-ember-500" />
              Portfolio insight
            </div>
            <p className="mt-1 text-[0.5rem] leading-relaxed text-slate-400">
              Idle USDC is ready to deploy.
            </p>
          </div>
        </div>
      </div>
    </DemoWindow>
  );
}

/** Streamline payments — a batch progresses from policy checks to completion. */
export function PaymentsGraphic() {
  const recipients = [
    {
      name: 'Atlas Labs',
      amount: '72,400 USDC',
      initials: 'AL',
      Icon: UsdcIcon,
    },
    { name: 'Northstar', amount: '58,000 USDC', initials: 'N', Icon: UsdcIcon },
    { name: 'Studio 08', amount: '20 ETH', initials: 'S8', Icon: EthIcon },
  ];

  return (
    <DemoWindow>
      <div className="flex h-full flex-col p-3.5">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[0.5rem] font-medium uppercase tracking-[0.12em] text-slate-400">
              Vendor batch
            </p>
            <p className="mt-0.5 text-sm font-semibold tracking-tight text-slate-900">
              $182,400
            </p>
          </div>
          <span className="cap-glass-warm-chip rounded-full bg-amber-50 px-2 py-1 text-[0.5rem] font-semibold text-amber-700">
            3 recipients
          </span>
        </div>

        <div className="mt-3 h-1 overflow-hidden rounded-full bg-slate-100">
          <div className="cap-payment-progress h-full rounded-full bg-ember-500" />
        </div>

        <div className="mt-2.5 space-y-1.5">
          {recipients.map(({ name, amount, initials, Icon }, index) => (
            <div
              key={name}
              className="cap-payment-row cap-glass-row flex items-center rounded-lg border border-slate-100 bg-slate-50/70 px-2 py-1.5"
              style={{ animationDelay: `${index * 700}ms` }}
            >
              <span className="cap-glass-chip grid size-5 place-items-center rounded-md bg-white text-[0.48rem] font-bold text-slate-500 shadow-sm ring-1 ring-slate-200">
                {initials}
              </span>
              <span className="ml-2 text-[0.53rem] font-semibold text-slate-700">
                {name}
              </span>
              <span className="ml-auto inline-flex items-center gap-1 text-[0.52rem] font-semibold text-slate-900">
                <Icon className="size-3 shrink-0" aria-hidden="true" />
                {amount}
              </span>
              <CheckCircle2 className="ml-1.5 size-3 text-emerald-500" />
            </div>
          ))}
        </div>

        <div className="mt-auto flex items-center justify-between border-t border-slate-100 pt-2.5">
          <span className="inline-flex items-center gap-1 text-[0.52rem] font-medium text-slate-500">
            <ShieldCheck className="size-3 text-emerald-600" />
            Policy approved
          </span>
          <span className="cap-send-button cap-glass-dark inline-flex items-center gap-1 rounded-md bg-slate-950 px-2.5 py-1.5 text-[0.5rem] font-semibold text-white">
            <Send className="size-2.5" />
            Send batch
          </span>
        </div>
      </div>
    </DemoWindow>
  );
}

/** Stablecoins and RWAs — an issuance request clears governance checks. */
export function IssuanceGraphic() {
  return (
    <DemoWindow>
      <div className="relative flex h-full flex-col overflow-hidden p-3.5">
        <div className="flex items-center gap-2.5">
          <span className="cap-glass-dark grid size-8 place-items-center rounded-xl bg-slate-950 text-[0.55rem] font-bold text-white">
            DY
          </span>
          <div>
            <div className="flex items-center gap-1.5">
              <span className="text-[0.62rem] font-semibold text-slate-900">
                Den Yield USD
              </span>
              <span className="cap-glass-success-chip rounded-full bg-emerald-50 px-1.5 py-0.5 text-[0.48rem] font-semibold text-emerald-700">
                LIVE
              </span>
            </div>
            <span className="text-[0.52rem] text-slate-400">
              Tokenized US Treasury fund
            </span>
          </div>
        </div>

        <div className="mt-3 grid grid-cols-2 gap-2">
          <div className="cap-glass-row rounded-lg bg-slate-50 p-2.5">
            <span className="text-[0.5rem] font-medium text-slate-400">
              Total supply
            </span>
            <span className="mt-0.5 block text-[0.72rem] font-semibold text-slate-900">
              $18.42M
            </span>
          </div>
          <div className="cap-glass-row rounded-lg bg-slate-50 p-2.5">
            <span className="text-[0.5rem] font-medium text-slate-400">
              Holders
            </span>
            <span className="mt-0.5 block text-[0.72rem] font-semibold text-slate-900">
              1,284
            </span>
          </div>
        </div>

        <div className="cap-mint-card cap-glass-warm mt-2.5 rounded-xl border border-ember-200/70 bg-ember-50/80 p-2.5 shadow-[0_10px_30px_-22px_rgba(237,78,0,0.8)]">
          <div className="flex items-center justify-between">
            <span className="inline-flex items-center gap-1.5 text-[0.5rem] font-semibold text-slate-700">
              <ArrowUpRight className="size-3 text-ember-600" />
              Mint request
            </span>
            <span className="text-[0.65rem] font-semibold text-slate-950">
              +2,000,000 DY
            </span>
          </div>
          <div className="mt-2 flex items-center gap-1.5">
            {['Governance', 'Compliance', 'Liquidity'].map((label, index) => (
              <span
                key={label}
                className="cap-issuance-check cap-glass-chip inline-flex min-w-0 flex-1 items-center gap-1 rounded-md bg-white/80 px-1.5 py-1 text-[0.47rem] font-medium text-slate-500 ring-1 ring-ember-100"
                style={{ animationDelay: `${index * 650}ms` }}
              >
                <Check className="size-2.5 shrink-0 text-emerald-600" />
                <span className="truncate">{label}</span>
              </span>
            ))}
          </div>
        </div>
      </div>
    </DemoWindow>
  );
}

/** Launch products — an SDK request creates an embedded customer wallet. */
export function DeveloperGraphic() {
  return (
    <DemoWindow>
      <div className="grid h-full grid-cols-[minmax(0,1.15fr)_minmax(7.5rem,0.85fr)]">
        <div className="cap-solid-code-pane min-w-0 bg-slate-950 p-3.5 font-mono text-white">
          <div className="flex items-center gap-1.5 text-[0.52rem] text-slate-400">
            <Code2 className="size-3 text-ember-400" />
            create-wallet.ts
          </div>
          <div className="mt-3 space-y-1.5 text-[0.52rem] leading-relaxed sm:text-[0.55rem]">
            <div>
              <span className="text-violet-300">const</span>{' '}
              <span className="text-sky-300">wallet</span>{' '}
              <span className="text-slate-500">=</span>{' '}
              <span className="text-violet-300">await</span>
            </div>
            <div className="pl-2 text-slate-200">
              den.wallets.<span className="text-amber-300">create</span>({'{'})
            </div>
            <div className="pl-4">
              <span className="text-sky-300">owner</span>
              <span className="text-slate-500">:</span>{' '}
              <span className="text-emerald-300">&quot;nora@acme.co&quot;</span>
              ,
            </div>
            <div className="pl-4">
              <span className="text-sky-300">network</span>
              <span className="text-slate-500">:</span>{' '}
              <span className="text-emerald-300">&quot;base&quot;</span>,
            </div>
            <div className="pl-4">
              <span className="text-sky-300">policy</span>
              <span className="text-slate-500">:</span>{' '}
              <span className="text-emerald-300">&quot;consumer&quot;</span>
            </div>
            <div className="pl-2 text-slate-200">{'}'});</div>
          </div>
          <div className="cap-api-response cap-glass-success-dark mt-3 flex items-center gap-1.5 rounded-md border border-emerald-500/20 bg-emerald-500/10 px-2 py-1.5 font-sans text-[0.5rem] font-medium text-emerald-300">
            <CheckCircle2 className="size-2.5" />
            201 · Wallet created in 284ms
          </div>
        </div>

        <div className="cap-glass-warm-pane relative flex min-w-0 flex-col bg-gradient-to-b from-white to-ember-50/70 p-3">
          <div className="flex items-center justify-between">
            <span className="text-[0.5rem] font-semibold text-slate-400">
              CUSTOMER WALLET
            </span>
            <span className="size-1.5 rounded-full bg-emerald-500" />
          </div>
          <div className="mt-2.5 flex items-center gap-2">
            <span className="grid size-7 place-items-center rounded-full bg-gradient-to-br from-ember-300 to-ember-600 text-[0.5rem] font-bold text-white">
              N
            </span>
            <div className="min-w-0">
              <span className="block truncate text-[0.58rem] font-semibold text-slate-900">
                Nora&apos;s wallet
              </span>
              <span className="block font-mono text-[0.47rem] text-slate-400">
                0x83…19f2
              </span>
            </div>
          </div>
          <div className="cap-glass-panel mt-3 rounded-xl border border-ember-100 bg-white p-2.5 shadow-sm">
            <span className="text-[0.49rem] text-slate-400">Balance</span>
            <span className="mt-0.5 block text-xs font-semibold tracking-tight text-slate-950 sm:text-sm">
              2,840 USDC
            </span>
            <div className="mt-2 flex flex-wrap gap-1">
              <span className="cap-glass-chip inline-flex items-center gap-1 rounded bg-slate-100 px-1.5 py-1 text-[0.47rem] font-semibold text-slate-600">
                <UsdcIcon className="size-2.5 shrink-0" aria-hidden="true" />
                USDC
              </span>
              <span className="cap-glass-chip inline-flex items-center gap-1 rounded bg-slate-100 px-1.5 py-1 text-[0.47rem] font-semibold text-slate-600">
                <EthIcon className="size-2.5 shrink-0" aria-hidden="true" />
                ETH
              </span>
            </div>
          </div>
          <div className="cap-wallet-toast cap-glass-dark absolute bottom-3 left-3 right-3 flex items-center gap-1.5 rounded-lg bg-slate-950 px-2 py-1.5 text-[0.5rem] font-medium text-white shadow-lg">
            <WalletCards className="size-3 text-ember-400" />
            Wallet ready
            <Check className="ml-auto size-3 text-emerald-400" />
          </div>
        </div>
      </div>
    </DemoWindow>
  );
}

/** Protocol management — a decoded admin call clears contract permissions. */
export function ProtocolGraphic() {
  return (
    <DemoWindow>
      <div className="flex h-full flex-col p-3.5">
        <div className="flex items-center gap-2.5">
          <span className="cap-glass-warm-chip grid size-7 place-items-center rounded-lg bg-ember-50 text-ember-700">
            <Code2 className="size-3.5" />
          </span>
          <div>
            <span className="block text-[0.57rem] font-semibold text-slate-900">
              Protocol operations
            </span>
            <span className="block text-[0.48rem] text-slate-400">
              Smart contract management
            </span>
          </div>
          <span className="cap-glass-success-chip ml-auto inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-1 text-[0.48rem] font-semibold text-emerald-700">
            <CheckCircle2 className="size-2.5" />
            Policy approved
          </span>
        </div>

        <div className="cap-glass-panel mt-3 rounded-xl border border-slate-200 bg-white p-3">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[0.55rem] font-semibold text-slate-700">
              Protocol controller
            </span>
            <span className="font-mono text-[0.47rem] text-slate-400">
              0x7a…6c91
            </span>
          </div>
          <div className="mt-2 rounded-md bg-slate-50 px-2 py-1.5 font-mono text-[0.55rem] text-slate-800">
            grantRole(role, account)
          </div>
          <div className="mt-2 grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 text-[0.5rem]">
            <span className="text-slate-400">Role</span>
            <span className="text-right font-medium text-slate-700">
              Operator
            </span>
            <span className="text-slate-400">Account</span>
            <span className="text-right font-medium text-slate-700">
              Operations wallet
            </span>
          </div>
        </div>

        <div className="mt-auto flex items-center justify-between gap-2 border-t border-slate-100 pt-2.5">
          <span className="inline-flex items-center gap-1 text-[0.5rem] font-medium text-slate-500">
            <ShieldCheck className="size-3 text-emerald-600" />
            Required approvals complete
          </span>
          <span className="cap-glass-dark inline-flex items-center gap-1 rounded-md bg-slate-950 px-2 py-1.5 text-[0.5rem] font-semibold text-white">
            Ready to execute <ArrowUpRight className="size-2.5" />
          </span>
        </div>
      </div>
    </DemoWindow>
  );
}

/** Make the chain invisible — a familiar send flow with an automated route. */
export function InvisibleChainGraphic() {
  return (
    <DemoWindow>
      <div className="flex h-full flex-col p-3.5">
        <div className="cap-glass-row flex items-center gap-2.5 rounded-xl border border-slate-100 bg-slate-50/70 p-2.5">
          <span className="cap-glass-dark grid size-8 place-items-center rounded-full bg-slate-900 text-[0.55rem] font-bold text-white">
            MC
          </span>
          <div>
            <span className="block text-[0.55rem] font-semibold text-slate-900">
              Maya Chen
            </span>
            <span className="block text-[0.5rem] text-slate-400">
              Product operations
            </span>
          </div>
          <span className="ml-auto text-[0.7rem] font-semibold text-slate-950">
            $1,250
          </span>
        </div>

        <div className="cap-glass-panel mt-3 rounded-xl border border-slate-100 p-2.5">
          <div className="flex items-center justify-between text-[0.52rem]">
            <span className="font-medium text-slate-500">Best route</span>
            <span className="font-semibold text-emerald-700">Automatic</span>
          </div>
          <div className="mt-2 flex items-center">
            <span className="grid size-6 place-items-center rounded-full bg-blue-600 text-[0.47rem] font-bold text-white">
              B
            </span>
            <div className="relative mx-1.5 h-px flex-1 bg-slate-200">
              <span className="cap-route-line absolute inset-y-0 left-0 bg-ember-500" />
              <span className="cap-route-dot absolute -top-1 size-2 rounded-full bg-ember-500 ring-2 ring-ember-100" />
            </div>
            <span className="grid size-6 place-items-center rounded-full bg-slate-800 text-[0.47rem] font-bold text-white">
              E
            </span>
          </div>
          <div className="mt-1 flex justify-between text-[0.47rem] text-slate-400">
            <span>Base</span>
            <span>Ethereum</span>
          </div>
        </div>

        <div className="mt-auto flex items-center justify-between">
          <span className="text-[0.5rem] text-slate-400">
            Network fee <strong className="text-slate-700">$0.00</strong>
          </span>
          <span className="cap-ready-button cap-glass-dark inline-flex items-center gap-1 rounded-md bg-slate-950 px-2.5 py-1.5 text-[0.52rem] font-semibold text-white">
            Review transfer <ArrowUpRight className="size-2.5" />
          </span>
        </div>
      </div>
    </DemoWindow>
  );
}

/** Control transactions with policies — rules evaluate across Den's security layers. */
export function PoliciesGraphic() {
  const rules = [
    ['Spending limit', '≤ $50,000'],
    ['Required approvals', '2 of 3'],
    ['Destination', 'Allowlisted'],
  ];

  return (
    <DemoWindow>
      <div className="flex h-full flex-col p-3.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="cap-glass-warm-chip grid size-7 place-items-center rounded-lg bg-ember-50 text-ember-700">
              <LockKeyhole className="size-3.5" />
            </span>
            <div>
              <span className="block text-[0.57rem] font-semibold text-slate-900">
                Transfer guard
              </span>
              <span className="block text-[0.48rem] text-slate-400">
                Applies to treasury wallets
              </span>
            </div>
          </div>
          <span className="cap-policy-toggle relative h-4 w-7 rounded-full bg-emerald-500">
            <span className="absolute right-0.5 top-0.5 size-3 rounded-full bg-white shadow-sm" />
          </span>
        </div>

        <div className="mt-3 space-y-1.5">
          {rules.map(([label, value], index) => (
            <div
              key={label}
              className="cap-policy-rule cap-glass-row flex items-center rounded-lg border border-slate-100 bg-slate-50/60 px-2.5 py-1.5"
              style={{ animationDelay: `${index * 700}ms` }}
            >
              <Check className="mr-2 size-3 text-emerald-600" />
              <span className="text-[0.52rem] font-medium text-slate-500">
                {label}
              </span>
              <span className="ml-auto text-[0.52rem] font-semibold text-slate-800">
                {value}
              </span>
            </div>
          ))}
        </div>

        <div className="cap-glass-dark mt-auto rounded-xl bg-slate-950 p-2.5 text-white">
          <div className="flex items-center justify-between">
            <span className="text-[0.5rem] font-medium text-slate-400">
              Transaction evaluation
            </span>
            <span className="text-[0.49rem] font-semibold text-emerald-400">
              PASSED
            </span>
          </div>
          <div className="mt-2 flex items-center gap-1.5">
            {[
              [UsersRound, 'Approvals'],
              [ShieldCheck, 'Cloud'],
              [FileCheck2, 'Onchain'],
            ].map(([Icon, label], index) => {
              const RuleIcon = Icon as typeof UsersRound;
              return (
                <div
                  key={label as string}
                  className="cap-security-layer cap-glass-dark-chip flex min-w-0 flex-1 items-center justify-center gap-1 rounded-md bg-white/5 px-1 py-1.5 text-[0.47rem] font-medium text-slate-300"
                  style={{ animationDelay: `${index * 650}ms` }}
                >
                  <RuleIcon className="size-2.5 shrink-0" />
                  <span className="truncate">{label as string}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </DemoWindow>
  );
}

/** Enterprise readiness — audit events and flexible deployment controls. */
export function EnterpriseGraphic() {
  const events = [
    ['10:42', 'Policy updated', 'I. Vidal'],
    ['10:39', 'Transfer approved', 'A. Jones'],
    ['10:35', 'Signer added', 'S. Patel'],
  ];

  return (
    <DemoWindow>
      <div className="flex h-full flex-col p-3.5">
        <div className="cap-glass-dark flex items-center justify-between rounded-xl bg-slate-950 px-2.5 py-2 text-white">
          <span className="inline-flex items-center gap-1.5 text-[0.5rem] font-semibold">
            <ShieldCheck className="size-3 text-emerald-400" />
            All systems operational
          </span>
          <span className="cap-glass-dark-chip rounded bg-white/10 px-1.5 py-1 text-[0.47rem] font-semibold text-slate-300">
            SOC 2 TYPE II
          </span>
        </div>

        <div className="mt-3 flex items-center justify-between">
          <span className="text-[0.5rem] font-semibold text-slate-700">
            Audit log
          </span>
          <span className="text-[0.48rem] text-slate-400">
            Immutable · 4,821 events
          </span>
        </div>

        <div className="mt-1.5 space-y-1">
          {events.map(([time, action, actor], index) => (
            <div
              key={action}
              className="cap-audit-row cap-glass-row grid grid-cols-[2.2rem_minmax(0,1fr)_2.5rem_0.75rem] items-center gap-1 rounded-md px-1.5 py-1.5 text-[0.49rem]"
              style={{ animationDelay: `${index * 720}ms` }}
            >
              <span className="font-mono text-slate-400">{time}</span>
              <span className="truncate font-medium text-slate-700">
                {action}
              </span>
              <span className="truncate text-slate-400">{actor}</span>
              <CheckCircle2 className="size-2.5 text-emerald-500" />
            </div>
          ))}
        </div>

        <div className="cap-glass-row mt-auto flex items-center rounded-lg border border-slate-200 bg-slate-50 p-1">
          {['Den Cloud', 'Your VPC', 'On-prem'].map((option, index) => (
            <span
              key={option}
              className={cn(
                'flex-1 rounded-md px-1 py-1.5 text-center text-[0.48rem] font-semibold',
                index === 1
                  ? 'cap-deployment-active cap-glass-chip bg-white text-slate-900 shadow-sm'
                  : 'text-slate-400'
              )}
            >
              {option}
            </span>
          ))}
        </div>
      </div>
    </DemoWindow>
  );
}

/* -------------------------------------------------------------------------- */
/*  Generic card                                                              */
/* -------------------------------------------------------------------------- */

function CapabilityCard({
  title,
  description,
  className,
  graphic,
}: {
  title: string;
  description: string;
  className?: string;
  graphic?: ReactNode;
}) {
  return (
    <div
      className={cn(
        'capability-card relative isolate flex h-full min-h-[24rem] flex-col overflow-hidden rounded-xl border-none border-white p-6 transition duration-300 hover:-translate-y-0.5 hover:shadow-soft md:min-h-0',
        className
      )}
    >
      <div className="relative z-10 shrink-0">
        <h3 className="text-2xl font-medium text-slate-900">{title}</h3>
        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-700">
          {description}
        </p>
      </div>
      {graphic ? (
        <div
          aria-hidden
          className="cap-demo-stage pointer-events-none relative z-0 -mx-2 -mb-2 mt-3 flex min-h-[14.25rem] w-[calc(100%+1rem)] flex-1 items-end"
        >
          {graphic}
        </div>
      ) : null}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Content + layout                                                          */
/* -------------------------------------------------------------------------- */

type Capability = {
  title: string;
  description: string;
  /** Grid-span classes for the md+ bento (applied to the grid item). */
  span?: string;
  /** Background + any per-card styling for the card's outer div. */
  className: string;
  /** Animated product demo shown in the card's dedicated visual area. */
  graphic: ReactNode;
};

const CAPABILITIES: Capability[] = [
  {
    title: 'Manage your treasury',
    description:
      'Hold any token across chains, or deploy them into onchain finance protocols.',
    span: 'md:col-span-2',
    className: 'bg-zinc-100/80 cap-dots border-slate-50',
    graphic: <TreasuryGraphic />,
  },
  {
    title: 'Streamline payments',
    description: 'Delegate payments without losing control.',
    className: 'bg-zinc-100/80 cap-grid border-slate-50',
    graphic: <PaymentsGraphic />,
  },
  {
    title: 'Issue stablecoins & RWAs',
    description: 'Manage issuance, governance, and liquidity.',
    className: 'bg-zinc-100/80 cap-dots border-slate-50',
    graphic: <IssuanceGraphic />,
  },
  {
    title: 'Launch digital asset products',
    description:
      'Provide user wallets directly in your product, from stablecoin neobanks to AI agents.',
    span: 'md:col-span-2',
    className: 'bg-ember-50 cap-glow-ember cap-dots border-ember-50',
    graphic: <DeveloperGraphic />,
  },
  {
    title: 'Make the chain invisible',
    description:
      'No gas fees, a shared address book, and human-readable transactions.',
    className: 'bg-zinc-100/80 cap-dots border-slate-50',
    graphic: <InvisibleChainGraphic />,
  },
  {
    title: 'Control transactions with policies',
    description: 'Set approvals, allowlists, and spending limits.',
    className: 'bg-zinc-100/80 cap-grid border-slate-50',
    graphic: <PoliciesGraphic />,
  },
  {
    title: 'Ready for Enterprise',
    description:
      'Compliant audit logs, SOC 2 Type II certified, and flexible deployment options.',
    className: 'bg-zinc-100/80 cap-dots border-slate-50',
    graphic: <EnterpriseGraphic />,
  },
];

export function Capabilities() {
  return (
    <Section className="bg-white">
      <Container>
        <h2 className="text-center text-3xl font-medium leading-relaxed tracking-tight text-slate-900 sm:text-left sm:text-4xl lg:text-[2.5rem]">
          Run secure onchain operations <br className="hidden sm:block" />
          <span className="text-slate-900">
            on enterprise-ready infrastructure
          </span>
        </h2>

        <div className="mt-12 grid grid-cols-1 gap-4 md:grid-cols-2 md:auto-rows-[minmax(24rem,auto)] lg:grid-cols-3">
          {CAPABILITIES.map((capability, i) => (
            <Reveal
              key={capability.title}
              delay={i * 70}
              className={capability.span ?? ''}
            >
              <CapabilityCard
                title={capability.title}
                description={capability.description}
                className={capability.className}
                graphic={capability.graphic}
              />
            </Reveal>
          ))}
        </div>
      </Container>
    </Section>
  );
}
